import { AlertTriangle, UserCheck, UserPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';

/**
 * PASSO 2 — "É O MESMO ATLETA?"
 *
 * O QUE MUDOU: antes, ao cadastrar alguém que já existia, o sistema GRAVAVA
 * assim mesmo e mostrava um aviso que desaparecia sozinho. As duas duplicatas
 * reais que existem em produção (mesmo nome E mesmo CPF válido) nasceram
 * exatamente assim: o aviso apareceu e passou.
 *
 * AGORA: o backend NÃO grava nada quando encontra alguém parecido — devolve
 * 409 com a lista, e esta tela faz a pergunta. Quem decide é a pessoa:
 *   * "É este"        -> abre o cadastro que já existe (nada novo é criado);
 *   * "É outro atleta"-> reenvia confirmando, e o cadastro é criado normal.
 *
 * Nada é bloqueado de verdade: o cadastro legítimo (irmãos, nomes parecidos,
 * turma inteira do professor) continua funcionando com um clique.
 */

const formatarData = (iso) => {
  if (!iso) return '';
  const [ano, mes, dia] = String(iso).slice(0, 10).split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : String(iso);
};

const ROTULO_MOTIVO = {
  cpf: 'Mesmo CPF',
  nome_nascimento: 'Mesmo nome e mesma data de nascimento',
};

export function DialogoDuplicata({
  aberto,
  atletas = [],
  nomeNovo = '',
  aoUsarExistente,
  aoCriarNovo,
  aoCancelar,
  processando = false,
}) {
  const lista = atletas || [];

  return (
    <Dialog
      open={aberto}
      onOpenChange={(v) => {
        // Fechar no X ou no Esc equivale a cancelar (nada é criado).
        if (!v && !processando) aoCancelar?.();
      }}
    >
      <DialogContent className="max-w-lg" data-testid="dialogo-duplicata">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[#D4AF37]" aria-hidden="true" />
            Já existe um atleta parecido
          </DialogTitle>
          <DialogDescription>
            {lista.length === 1
              ? 'Encontramos 1 cadastro que parece ser a mesma pessoa'
              : `Encontramos ${lista.length} cadastros que parecem ser a mesma pessoa`}
            {nomeNovo ? ` que você está cadastrando (${nomeNovo})` : ''}.
            {' '}Nada foi salvo ainda — confirme abaixo.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 flex flex-col gap-2 max-h-72 overflow-y-auto" data-testid="lista-duplicatas">
          {lista.map((a) => (
            <div
              key={a._id}
              className="flex items-start justify-between gap-3 rounded-lg border border-[#27272A] bg-[#141414] p-3"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-white">{a.name}</p>
                <p className="text-xs text-[#A1A1AA]">
                  {[formatarData(a.birth_date), a.team_name].filter(Boolean).join(' · ')}
                </p>
                <p className="mt-1 text-micro text-[#71717A]">
                  {ROTULO_MOTIVO[a.motivo] || 'Dados parecidos'}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={processando}
                onClick={() => aoUsarExistente?.(a)}
                data-testid={`usar-existente-${a._id}`}
                className="shrink-0 border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              >
                <UserCheck size={14} className="mr-1.5" aria-hidden="true" />
                É este
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <Button
            variant="outline"
            disabled={processando}
            onClick={aoCriarNovo}
            data-testid="confirmar-novo-atleta"
            className="border-[#27272A] text-[#A1A1AA] hover:text-white"
          >
            <UserPlus size={16} className="mr-2" aria-hidden="true" />
            É outro atleta — criar um cadastro novo
          </Button>
          <Button
            variant="ghost"
            disabled={processando}
            onClick={aoCancelar}
            data-testid="cancelar-duplicata"
            className="text-[#71717A] hover:text-white"
          >
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DialogoDuplicata;
